<script setup lang="ts">
import type { ITableHeader } from "@/components/ui/Table/table.interface"
import type { SlideshowConfig } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useQueryClient } from "@tanstack/vue-query"
import { useHead } from "@unhead/vue"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, ref, useTemplateRef, watchEffect } from "vue"
import { useGetSlideshowConfig } from "@/api"
import { getSlideshowConfig } from "@/api/getSlideshowConfig"
import { useListSlideshowConfigs } from "@/api/listSlideshowConfigs"
import {
  Slideshow,
  SlideshowDeleteModal,
  SlideshowExportDrawer,
} from "@/components"
import {
  AppLink,
  Button,
  ButtonLink,
  Icon,
  NoData,
  Table,
  Toast,
  Tooltip,
} from "@/components/ui"
import { useConvertedRouteParam, useModal, useSlideshow, useTranslation } from "@/composables"
import { useFilters } from "@/composables/useFilters"
import { usePagination } from "@/composables/usePagination"
import { SidebarHostEventLayout } from "@/layouts"
import { SlideshowDeleteModalKey, SlideshowExportDrawerKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { d, t } = useTranslation()
const eventVaultId = useConvertedRouteParam("id", "/event/[id]/slideshow/", value => Number(value))
const queryClient = useQueryClient()
const successToast = useTemplateRef<ToastHtmlElement>("successToast")
const urlParams = useUrlSearchParams<{ export?: unknown }>("history")

const currentSlideshowId = ref<string>("")
const selectedSlideshowConfig = ref<SlideshowConfig | undefined>()
const selectedVideoSlideshowConfig = ref<SlideshowConfig | undefined>()
const slideshowIntervals = ref<number>(5000)

const { filters: slideshowListFilters } = useFilters({ page: 1, perPage: 10 })
const { data: slideshowList, isLoading, pageCount: slideshowListPageCount } = useListSlideshowConfigs(eventVaultId, slideshowListFilters)
const slideshowListPagination = usePagination({ initialPerPage: 10, pageCount: slideshowListPageCount })

watchEffect(() => {
  slideshowListFilters.page = slideshowListPagination.page.value
})

watchEffect(() => {
  if (slideshowListPageCount.value && slideshowListPagination.page.value > slideshowListPageCount.value)
    slideshowListPagination.page.value = slideshowListPageCount.value
})

const slideshows = computed(() => slideshowList.value?.slideshows ?? [])
const slideshowVideosEnabled = computed(() => slideshowList.value?.slideshowVideosEnabled ?? false)

const {
  data: currentSlideshow,
  refetch: refetchSlideshowConfig,
  isPending: isLoadingSlideshowConfig,
} = useGetSlideshowConfig(currentSlideshowId, eventVaultId)

const shouldLiveRefresh = computed(() => {
  return currentSlideshow?.value?.live ?? false
})

const slideInterval = computed(() => {
  return currentSlideshow?.value?.interval ?? 3000
})

const headers: ITableHeader<SlideshowConfig & { actions?: unknown }>[] = [
  { label: t("host_slideshows.headers.name"), value: "name" },
  { label: t("host_slideshows.headers.media_count"), value: "mediaCount" },
  { label: t("host_slideshows.headers.last_updated"), value: "lastUpdated" },
  { label: t("host_slideshows.headers.actions"), value: "actions" },
]

const { show, visible, initialise, isInitialising: isLoadingSlideshowMedia, setIsLive } = useSlideshow(currentSlideshowId, {
  speed: slideInterval,
})

const { toggle: toggleSlideshowDelete } = useModal(SlideshowDeleteModalKey)
const { isActive: isSlideshowExportActive, toggle: toggleSlideshowExport } = useModal(SlideshowExportDrawerKey)

watchEffect(() => {
  setIsLive(shouldLiveRefresh.value)
})

watchEffect(() => {
  if (!visible.value) {
    // when slideshow is closed, clear the current slideshow id
    // this also ensures that the live refresh interval in useSlideshow is stopped
    currentSlideshowId.value = ""
  }
})

function onSlideshowDeleteToggle(slideshowConfig: SlideshowConfig) {
  selectedSlideshowConfig.value = slideshowConfig
  toggleSlideshowDelete()
}

function onSlideshowExportToggle(slideshowConfig: SlideshowConfig) {
  selectedVideoSlideshowConfig.value = slideshowConfig
  toggleSlideshowExport()
}

async function openSlideshowExportFromLink(slideshowId: string, slideshowEventVaultId: number) {
  const slideshowConfig = await getSlideshowConfig(slideshowId, slideshowEventVaultId).catch(() => undefined)

  if (slideshowConfig) {
    selectedVideoSlideshowConfig.value = slideshowConfig

    if (!isSlideshowExportActive.value)
      toggleSlideshowExport()
  }

  delete urlParams.export
}

watchEffect(() => {
  if (urlParams.export && typeof urlParams.export === "string" && eventVaultId.value)
    openSlideshowExportFromLink(urlParams.export, eventVaultId.value)
})

function onSlideshowDeleteSuccess() {
  successToast.value?.showToast({
    title: t("success"),
    content: t("slideshow_delete_modal.success_content"),
    icon: "CircleCheck",
  })

  queryClient.invalidateQueries({ queryKey: ["listSlideshowConfigs"] })
}

watchEffect(() => {
  slideshowIntervals.value = currentSlideshow.value?.interval ?? 5000
})

async function onSlideshowPlay(id?: string) {
  if (!id) {
    throw new Error("Missing id, unable to start slideshow")
  }

  currentSlideshowId.value = id
  refetchSlideshowConfig()
  await initialise()
  show()
}

useHead({ title: t("host_slideshows.title") })
</script>

<template>
  <SidebarHostEventLayout>
    <div class="flex flex-col w-full gap-4">
      <div>
        <div class="flex flex-col gap-4">
          <div class="flex justify-between gap-4 items-center flex-wrap">
            <h1 class="font-comfortaa font-bold text-2xl text-[#072A47]" data-test="slideshow-list-heading">
              {{ t("host_slideshows.title") }}
            </h1>
            <ButtonLink :to="`/event/${eventVaultId}/slideshow/create`" prefix-icon="CirclePlus" data-test="create-slideshow-btn">
              <span class="sr-only md:not-sr-only">{{ t("host_slideshows.create") }}</span>
            </ButtonLink>
          </div>
          <Table
            :headers="headers"
            :data="slideshows as Array<SlideshowConfig & { actions?: unknown }>"
            :is-loading="isLoading"
            :pagination="slideshowListPagination"
            :show-pagination="slideshowListPageCount > 1"
            dense
            @pagination-next="slideshowListPagination.next"
            @pagination-previous="slideshowListPagination.previous"
          >
            <template #empty>
              <tbody>
                <tr>
                  <td :colspan="headers.length + 1">
                    <NoData
                      :headline="t('host_slideshows.empty_title')"
                      :content="t('host_slideshows.empty_desc', { button: t('host_slideshows.create') })"
                      hide-button
                    />
                  </td>
                </tr>
              </tbody>
            </template>

            <template #data-name="{ data }">
              <div class="font-bold font-comfortaa text-lg" data-test="slideshow-name">
                {{ data?.name ? data?.name : "—" }}
              </div>
            </template>

            <template #data-mediaCount="{ data }">
              <div class="flex items-center gap-3">
                <span>{{ data?.mediaCount ?? 0 }}</span>
                <Tooltip v-if="typeof data.mediaCount === 'number' && data.mediaCount <= 1">
                  <Icon icon="Caution" class="text-amber-600" />
                  <template #content>
                    <div class="max-w-56">
                      {{ t('host_slideshows.length_warning') }}
                    </div>
                  </template>
                </Tooltip>
              </div>
            </template>

            <template #data-lastUpdated="{ data }">
              <span v-if="data.lastUpdated">{{ d(data.lastUpdated, "short") }}</span>
            </template>

            <template #data-actions="{ data }">
              <div class="flex items-center gap-2">
                <div class="hidden md:flex">
                  <Button
                    :disabled="!data.mediaCount"
                    prefix-icon="Play" variant="tertiary"
                    data-test="play-slideshow"
                    @click="() => onSlideshowPlay(data.id)"
                  >
                    {{ t("host_slideshows.play") }}
                  </Button>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    class="inline cursor-pointer transition-opacity hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-40 md:hidden"
                    :disabled="!data.mediaCount"
                    @click="() => onSlideshowPlay(data.id)"
                  >
                    <Icon icon="Play" />
                    <span class="sr-only">{{ t("host_slideshows.play") }}</span>
                  </button>
                  <AppLink
                    v-if="eventVaultId && data.id"
                    :to="`/event/${eventVaultId}/slideshow/${data.id}`"
                    class="cursor-pointer transition-opacity hover:opacity-60"
                    data-test="edit-slideshow"
                  >
                    <Icon icon="Edit" />
                    <span class="sr-only">
                      {{ t("host_slideshows.edit", { name: data.name }) }}
                    </span>
                  </AppLink>
                  <button
                    v-if="slideshowVideosEnabled"
                    class="cursor-pointer transition-opacity hover:opacity-60"
                    data-test="download-slideshow"
                    @click="() => onSlideshowExportToggle(data)"
                  >
                    <Icon icon="Download" />
                    <span class="sr-only">
                      {{ t("host_slideshows.download", { name: data.name }) }}
                    </span>
                  </button>
                  <button
                    class="cursor-pointer transition-opacity hover:opacity-60"
                    data-test="delete-slideshow"
                    @click="() => onSlideshowDeleteToggle(data)"
                  >
                    <Icon icon="Trash" />
                    <span class="sr-only">
                      {{ t("host_slideshows.delete", { name: data.name }) }}
                    </span>
                  </button>
                </div>
              </div>
            </template>
          </Table>
        </div>
      </div>
    </div>
  </SidebarHostEventLayout>

  <SlideshowDeleteModal
    v-if="selectedSlideshowConfig" :slideshow-config="selectedSlideshowConfig"
    :event-vault-id="eventVaultId" @close="selectedSlideshowConfig = undefined"
    @success="onSlideshowDeleteSuccess"
  />

  <SlideshowExportDrawer
    :slideshow-config="selectedVideoSlideshowConfig"
    :event-vault-id="eventVaultId"
  />

  <Slideshow
    v-if="currentSlideshow"
    :is-loading="isLoadingSlideshowMedia || isLoadingSlideshowConfig"
    :slideshow-config="currentSlideshow"
    transition-effect="slideshow-fade"
    autoplay
  />

  <Toast ref="successToast" variant="success" />
</template>
